import React, { useCallback, useEffect, useState } from 'react';
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  X, 
  Check, 
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { useUserData } from '../contexts/UserDataContext';
import * as utils from '../lib/utils.js';
// Mock data for users
const mockUsers = [
  { 
    id: 1, 
    name: 'John Doe', 
    email: 'john.doe@example.com', 
    role: 'Admin', 
    department: 'IT',
    status: 'Active',
    lastLogin: '2025-03-10T10:30:00'
  },
  { 
    id: 2, 
    name: 'Jane Smith', 
    email: 'jane.smith@example.com', 
    role: 'Manager', 
    department: 'Operations',
    status: 'Active',
    lastLogin: '2025-03-09T14:45:00'
  },
  { 
    id: 3, 
    name: 'Robert Johnson', 
    email: 'robert.johnson@example.com', 
    role: 'Viewer', 
    department: 'Finance',
    status: 'Inactive',
    lastLogin: '2025-02-28T09:15:00'
  },
  { 
    id: 4, 
    name: 'Emily Davis', 
    email: 'emily.davis@example.com', 
    role: 'Manager', 
    department: 'Sales',
    status: 'Active',
    lastLogin: '2025-03-11T08:20:00'
  },
  { 
    id: 5, 
    name: 'Michael Wilson', 
    email: 'michael.wilson@example.com', 
    role: 'Viewer', 
    department: 'Marketing',
    status: 'Active',
    lastLogin: '2025-03-10T16:05:00'
  }
];

// Available roles for dropdown
const roles = ['Admin', 'Employee', 'Forwarder'];

// Available departments for dropdown
const departments = ['Napco Security Technologies', 'Cloudbase Services', 'Elbon Logistics', 'Alba Wheels Up', "Fashion Forward"];

const UserManagement = () => {
  const { userData } = useUserData();
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [newUser, setNewUser] = useState({
    full_name: '',
    email: '',
    role: 'Employee',
    company_type: ''
  });
  const [emailError, setEmailError] = useState('');
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [isUpdatingUser, setIsUpdatingUser] = useState(false);
// {
//     "email": "apecora@napcosecurity.com",
//     "full_name": "Anthony Pecora",
//     "company_name": "Napco Security Technologies",
//     "company_type": "Napco Security",
//     "role": "Employee",
//     "fireauth_id": ""
// }
  const fetchUsers = useCallback(async () => {
    setIsLoadingUsers(true);
    try {
      const res = await utils.getPeople();
      const fetchedUsers = res.data.peoples.map(user => ({
        department: user.department,
        ...user
      }));
      setUsers(fetchedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);
  // Filter users based on search term only
  const filteredUsers = users.filter(user => {
    return user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) || 
           user.email.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Check if email already exists in the users list
  const checkEmailExists = (email) => {
    return users.some(user => user.email?.toLowerCase() === email?.toLowerCase());
  };

  // Handle adding a new user - map to the backend's expected format
  const handleAddUser = async () => {
    if (!newUser.full_name || !newUser.email || !newUser.role) {
      return; // Form validation
    }

    // Check for duplicate email before submitting
    if (checkEmailExists(newUser.email)) {
      setEmailError('A user with this email already exists');
      return;
    }

    setIsAddingUser(true);
    try {
      const userToAdd = {
        email: newUser.email,
        full_name: newUser.full_name,
        company_name: newUser.company_type,
        role: newUser.role,
        status: 'Active', // Default status
        fireauth_id: "" // New users might not have this initially
      };

      await utils.createPeople([userToAdd]);

      // Update state with the new user
      setUsers(prevUsers => [...prevUsers, userToAdd]);

      // Reset form
      setNewUser({
        full_name: '',
        email: '',
        role: 'Employee',
        company_type: '',
        status: 'Active'
      });

      // Close the modal
      setShowAddUserModal(false);

      console.log('User added successfully:', userToAdd);
    } catch (error) {
      console.error('Error adding user:', error);
      // Handle error (show notification, etc.)
    } finally {
      setIsAddingUser(false);
    }
  };

  // Handle updating a user
  const handleUpdateUser = async () => {
    if (!currentUser) return;
    setIsUpdatingUser(true);
    try {
      await utils.createPeople([currentUser]);
      setUsers(users.map(user =>
        user.email === currentUser.email ? currentUser : user
      ));
    } catch(error){
      console.error('Error updating user:', error);
    } finally {
      setIsUpdatingUser(false);
      setShowEditUserModal(false);
      setCurrentUser(null);
    }
  };

  // Handle deleting a user
  const handleDeleteUser = async () => {
    if (!currentUser) return;
    //make call to backend
    try { 
      const res = await utils.deletePeople([currentUser]);
      setUsers(users.filter(user => user.email !== currentUser.email));
      console.log('User deleted:', res.data);
    } catch (error){
      console.error('Error deleting user:', error);
    }
    setShowDeleteConfirmation(false);
    setCurrentUser(null);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Reset search
  const resetSearch = () => {
    setSearchTerm('');
  };

  // Manually refresh the users list shown in the table.
  const handleRefreshUsers = async () => {
    await fetchUsers();
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">User Management</h1>
          <div className="flex items-center">
            <span className="text-sm text-gray-600 mr-2">
              Your role: {userData?.role || 'Loading...'}
            </span>
            <button
              onClick={handleRefreshUsers}
              disabled={isLoadingUsers}
              className={utils.cn(
                "inline-flex items-center text-xs text-accent-600 hover:text-accent-800",
                "border border-accent-200 rounded px-2 py-1",
                isLoadingUsers && "opacity-50 cursor-not-allowed"
              )}
              title="Refresh user list"
            >
              <RefreshCw size={12} className={utils.cn(
                "mr-1",
                isLoadingUsers && "animate-spin"
              )} />
              Refresh Users
            </button>
          </div>
        </div>
        <button
          onClick={() => {
            setEmailError('');
            setShowAddUserModal(true);
          }}
          className="px-4 py-2 bg-accent-600 text-white rounded-md flex items-center hover:bg-accent-700 transition-colors"
        >
          <Plus size={16} className="mr-2" />
          Add User
        </button>
      </div>

      {/* Search */}
      <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex gap-2">
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={18} className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search users..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          {searchTerm && (
            <button
              onClick={resetSearch}
              className="px-3 py-2 border border-gray-300 text-gray-700 rounded-md flex items-center hover:bg-gray-50 transition-colors"
            >
              <X size={16} className="mr-2" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Users table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                {/* <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th> */}
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoadingUsers ? (
                <tr>
                  <td colSpan="7" className="px-6 py-10 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <RefreshCw size={36} className="text-gray-400 mb-3 animate-spin" />
                      <p className="text-sm">Loading users...</p>
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr key={user.email} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={utils.cn(
                          "flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center",
                          user.role === 'Admin' ? "bg-purple-100 text-purple-800" :
                          user.role === 'Manager' ? "bg-blue-100 text-blue-800" :
                          user.role === 'Forwarder' ? "bg-yellow-100 text-yellow-800" :
                          user.role === 'Employee' ? "bg-orange-100 text-red-800" :
                          "bg-green-100 text-green-800"
                        )}>
                          <span className="font-medium text-sm">
                            {user.full_name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={utils.cn(
                        "px-2 inline-flex text-xs leading-5 font-semibold rounded-full",
                        user.role === 'Admin' ? "bg-purple-100 text-purple-800" :
                        user.role === 'Manager' ? "bg-blue-100 text-blue-800" :
                        user.role === 'Forwarder' ? "bg-yellow-100 text-yellow-800" :
                        user.role === 'Employee' ? "bg-orange-100 text-red-800" :
                        "bg-green-100 text-green-800"
                      )}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.company_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={utils.cn(
                        "px-2 inline-flex text-xs leading-5 font-semibold rounded-full",
                        user.status === 'Active' ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                      )}>
                        {user.status}
                      </span>
                    </td>
                    {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.lastLogin)}
                    </td> */}
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => {
                            setCurrentUser(user);
                            setShowEditUserModal(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-gray-100"
                          title="Edit user"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setCurrentUser(user);
                            setShowDeleteConfirmation(true);
                          }}
                          className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-gray-100"
                          title="Delete user"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-10 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <AlertCircle size={48} className="text-gray-400 mb-4" />
                      <p className="text-lg font-medium">No users found</p>
                      <p className="text-sm">Try adjusting your search criteria</p>
                      {searchTerm && (
                        <button
                          onClick={resetSearch}
                          className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                        >
                          Clear search
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 text-sm text-gray-500">
          Showing {filteredUsers.length} of {users.length} users
        </div>
      </div>

      {/* Add User Modal - Updated for new fields */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex justify-between items-center border-b border-gray-200 p-4">
              <h3 className="text-lg font-medium text-gray-900">Add New User</h3>
              <button
                onClick={() => setShowAddUserModal(false)}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                  className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                  placeholder="Enter full name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => {
                    setNewUser({...newUser, email: e.target.value});
                    if (emailError) setEmailError('');
                  }}
                  className={utils.cn(
                    "w-full border rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500",
                    emailError ? "border-red-500" : "border-gray-300"
                  )}
                  placeholder="Enter email address"
                />
                {emailError && (
                  <p className="text-red-500 text-sm mt-1">{emailError}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                >
                  {roles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                <select
                  value={newUser.company_type}
                  onChange={(e) => setNewUser({...newUser, company_type: e.target.value})}
                  className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                >
                  <option value="">Select Company</option>
                  {departments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
              
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <div className="flex space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      className="form-radio text-accent-600 focus:ring-accent-500"
                      name="status"
                      value="Active"
                      checked={newUser.status === 'Active'}
                      onChange={() => setNewUser({...newUser, status: 'Active'})}
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      className="form-radio text-accent-600 focus:ring-accent-500"
                      name="status"
                      value="Inactive"
                      checked={newUser.status === 'Inactive'}
                      onChange={() => setNewUser({...newUser, status: 'Inactive'})}
                    />
                    <span className="ml-2 text-sm text-gray-700">Inactive</span>
                  </label>
                </div>
              </div> */}
            </div>
            
            <div className="bg-gray-50 px-4 py-3 flex justify-end space-x-2 rounded-b-lg">
              <button
                onClick={() => setShowAddUserModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
              >
                Cancel
              </button>
              <button
                onClick={handleAddUser}
                className="px-4 py-2 text-sm font-medium text-white bg-accent-600 border border-transparent rounded-md hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                disabled={!newUser.full_name || !newUser.email || !newUser.role || emailError || isAddingUser}
              >
                {isAddingUser && <RefreshCw size={14} className="mr-2 animate-spin" />}
                {isAddingUser ? 'Adding...' : 'Add User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && currentUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex justify-between items-center border-b border-gray-200 p-4">
              <h3 className="text-lg font-medium text-gray-900">Edit User</h3>
              <button
                onClick={() => {
                  setShowEditUserModal(false);
                  setCurrentUser(null);
                }}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={currentUser.full_name}
                  onChange={(e) => setCurrentUser({...currentUser, full_name: e.target.value})}
                  className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={currentUser.email}
                  disabled
                  className="w-full border border-gray-300 rounded-md p-2 bg-gray-100 text-gray-700 cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500 italic">Email addresses cannot be changed</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={currentUser.role}
                  onChange={(e) => setCurrentUser({...currentUser, role: e.target.value})}
                  className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                >
                  {roles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                <select
                  value={currentUser.company_name}
                  onChange={(e) => setCurrentUser({...currentUser, company_name: e.target.value})}
                  className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                >
                  {departments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={currentUser.status || 'Inactive'}
                  onChange={(e) => setCurrentUser({...currentUser, status: e.target.value})}
                  className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
            
            <div className="bg-gray-50 px-4 py-3 flex justify-end space-x-2 rounded-b-lg">
              <button
                onClick={() => {
                  setShowEditUserModal(false);
                  setCurrentUser(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateUser}
                disabled={isUpdatingUser}
                className="px-4 py-2 text-sm font-medium text-white bg-accent-600 border border-transparent rounded-md hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
              >
                {isUpdatingUser && <RefreshCw size={14} className="mr-2 animate-spin" />}
                {isUpdatingUser ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && currentUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                <AlertCircle size={24} className="text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-center text-gray-900 mb-2">
                Delete User
              </h3>
              <p className="text-sm text-center text-gray-500 mb-6">
                Are you sure you want to delete {currentUser.name}? This action cannot be undone.
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirmation(false);
                    setCurrentUser(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
